import React, { useEffect, useMemo, useState } from "react";
import "../PlanCreationModal.scss";
import { connect } from "react-redux";
import {
  Card,
  Collapse,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Fab,
  Typography,
} from "@mui/material";
import { DeleteOutlined as DeleteIcon } from "@mui/icons-material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

import bucksImg from "../../../../assets/plancreation/bucks.png";
import arrowDownIcon from "../../../../assets/plancreation/arrowdown.png";
import addIcon from "../../../../assets/add.png";

import Dropdown from "./Dropdown";
import SelectYear from "./SelectYear";
import SelectMonth from "./SelectMonth";
import TextInput from "./TextInput";

import {
  checkDateRangeValid,
  getDefaultLifeExpectancyDate,
  getLifeExpectancyDate,
  getRetireDate,
  hasCoClient,
} from "../utils";
import { getCurrentMonth, getCurrentYear } from "../../../../common/shared";

const columns = [
  { id: "type", label: "Type of expense", minWidth: 170 },
  {
    id: "monthly",
    label: "Amount",
    minWidth: 170,
  },
  { id: "owner", label: "Owner", minWidth: 160 },
  {
    id: "timeInterval",
    label: "Time interval",
    minWidth: 300,
  },
];

const ExpensesTab = ({
  currentClient,
  updateExpenses,
  data,
  changed,
  deleteItem,
  hideBeforeRetirement,
  hideDeathOfSpouse,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [categories, setCategories] = useState(data ? data : []);
  const [selectedCategoryId, setSelectedCategoryId] = useState(-1);

  // client details
  const client = useMemo(() => currentClient.get("client").toJS(), [
    currentClient,
  ]);
  const coClient = useMemo(() => currentClient.get("coClient").toJS(), [
    currentClient,
  ]);
  let isWidowEarlierThanRetirement = false;
  if (coClient && coClient.id !== undefined) {
    const clientRetirementDate = getRetireDate(client, true);
    const coClientLifeExpectancyDate = getLifeExpectancyDate(coClient, true);
    if (clientRetirementDate.getTime() > coClientLifeExpectancyDate.getTime()) {
      isWidowEarlierThanRetirement = true;
    }
  }
  const clients =
    coClient.id !== undefined
      ? [client, coClient, { id: 0, firstName: "Household", lastName: "" }]
      : [client];

  const calculateMaximumPlanLength = (item) => {
    if (item.owner === null) return 30;

    const lifeExpectancyDate = getDefaultLifeExpectancyDate(
      item.owner.id,
      client,
      coClient
    );

    const lifeExpectancyYear = lifeExpectancyDate.getFullYear();
    const currentYear = getCurrentYear();

    return lifeExpectancyYear - currentYear + 1;
  };

  const updateDefaultDates = (category, ownerId) => {
    const defaultEndDate = getDefaultLifeExpectancyDate(
      ownerId,
      client,
      coClient
    );
    return {
      ...category,
      endYear: defaultEndDate.getFullYear(),
      endMonth: defaultEndDate.getMonth() + 1,
    };
  };

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const addNewCategory = () => {
    const len = categories.length;
    if (len === 0 || categories[len - 1].name) {
      setCategories([
        ...categories,
        updateDefaultDates(
          {
            name: "",
            checked: false,
            owner: hasCoClient(client, coClient)
              ? { id: 0, firstName: "Household", lastName: "" }
              : {
                  id: client.id,
                  firstName: client.firstName,
                  lastName: client.lastName,
                },
            monthlyamount: "0",
            startYear: getCurrentYear(),
            startMonth: getCurrentMonth() + 1,
            endYear: getCurrentYear(),
            endMonth: getCurrentMonth() + 1,
            isEdited: false,
          },
          null
        ),
      ]);
      setSelectedCategoryId(len);
      changed();
    }
  };

  const updateCategory = (index, fieldName, value) => {
    const category = categories[index];
    categories.splice(index, 1);
    if (fieldName === "name") {
      categories.splice(index, 0, {
        ...category,
        name: value,
        isEdited: true,
      });
    } else if (fieldName === "ownerId") {
      const updatedCategory = updateDefaultDates(category, value);
      categories.splice(index, 0, {
        ...updatedCategory,
        owner: clients.find((c) => c.id === value),
        isEdited: true,
      });
    } else if (fieldName === "amount") {
      categories.splice(index, 0, {
        ...category,
        amount: value,
        isEdited: true,
      });
    } else if (fieldName === "startYear") {
      categories.splice(index, 0, {
        ...category,
        startYear: Number(value),
        isEdited: true,
      });
    } else if (fieldName === "startMonth") {
      categories.splice(index, 0, {
        ...category,
        startMonth: Number(value),
        isEdited: true,
      });
    } else if (fieldName === "endYear") {
      categories.splice(index, 0, {
        ...category,
        endYear: Number(value),
        isEdited: true,
      });
    } else if (fieldName === "endMonth") {
      categories.splice(index, 0, {
        ...category,
        endMonth: Number(value),
        isEdited: true,
      });
    }
    setCategories([...categories]);
    changed();
  };

  const deleteCategory = (index) => {
    deleteItem(categories[index]).then(() => {
      categories.splice(index, 1);
      setCategories([...categories]);
      changed();
    });
  };

  useEffect(() => {
    updateExpenses(categories);
  }, [categories]);

  return (
    <Grid container id="expenses-tab" direction="column" md={9}>
      <Grid item>
        <Typography component="h5">
          Let's calculate your client's expenses
        </Typography>
      </Grid>
      <Grid item>
        <Card className="monthly-expenses-card">
          <Typography className="highlighted-text" component="h6">
            Estimated Monthly Expenses
          </Typography>
          <Grid
            container
            spacing={4}
            justifyContent="space-between"
            className="monthly-expenses-inputs"
          >
            {!hideBeforeRetirement && (
              <Grid item>
                <Typography component={"label"}>
                  From now until Retirement
                </Typography>
                <TextInput
                  type="number"
                  id="input-before-retirement"
                  label=""
                  value={categories[0].amount}
                  adornment="$"
                  onChange={(e) => updateCategory(0, "amount", e.target.value)}
                />
              </Grid>
            )}
            <Grid item>
              <Typography component={"label"}>
                During Retirement
                {isWidowEarlierThanRetirement && (
                  <Tooltip
                    title={
                      'Based on the client and co-client\'s life expectancy settings, the widow phase would supersede retirement phase of the surviving client/co-client. Hence, "During Retirement" value is disable. If required, please use the detailed expense section to further specify the detailed expense items according to the applicable dates.'
                    }
                    placement="right"
                  >
                    <IconButton className={"report-icon"}>
                      <ReportProblemIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Typography>
              <TextInput
                type="number"
                id="input-after-retirement"
                label=""
                value={categories[1].amount}
                adornment="$"
                onChange={(e) => updateCategory(1, "amount", e.target.value)}
                readOnly={isWidowEarlierThanRetirement}
              />
            </Grid>
            {!hideDeathOfSpouse && (
              <Grid item>
                <Typography component={"label"}>Widowed Phase</Typography>
                <Tooltip
                  title={
                    "Widowed Phase refers to the period of life after ones spouse or partner has passed."
                  }
                >
                  <Fab className={"widowed-tooltip"}>?</Fab>
                </Tooltip>
                <TextInput
                  type="number"
                  id="input-after-spouse"
                  label=""
                  value={categories[2].amount}
                  adornment="$"
                  onChange={(e) => updateCategory(2, "amount", e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </Card>
        <Card className="expenses-detailed-card" onClick={handleExpand}>
          <Grid item xs={12} container direction="row" alignItems="center">
            <Grid item container xs spacing="2" alignItems="center">
              <Grid item>
                <img
                  className="arrow-down-icon"
                  src={arrowDownIcon}
                  alt="arrow-down-icon"
                />
              </Grid>
              <Grid item>
                <img className="bucks-img" src={bucksImg} alt="bucks-img" />
              </Grid>
              <Grid item>
                <Typography component="h6">
                  Add Detailed Monthly Expenses
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Card>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.slice(3).map((category, index) => (
                  <React.Fragment>
                    <TableRow key={index}>
                      <TableCell>
                        <Grid
                          item
                          container
                          direction="row"
                          wrap="nowrap"
                          alignItems="center"
                        >
                          <Grid item>
                            {index >= 6 ? (
                              <TextInput
                                label="New category"
                                value={category.name}
                                onChange={(e) =>
                                  updateCategory(
                                    index + 3,
                                    "name",
                                    e.target.value
                                  )
                                }
                                isRequired={category.isEdited}
                              />
                            ) : (
                              <Grid
                                item
                                container
                                direction="row"
                                alignItems="center"
                              >
                                <Typography className="expense-type">
                                  {category.name}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </TableCell>

                      <TableCell>
                        <Grid item>
                          <TextInput
                            type="number"
                            value={category.amount}
                            adornment="$"
                            onChange={(e) =>
                              updateCategory(
                                index + 3,
                                "amount",
                                e.target.value
                              )
                            }
                            isRequired={category.isEdited}
                          />
                        </Grid>
                      </TableCell>
                      <TableCell>
                        <Grid item>
                          <Dropdown
                            value={category.owner ? category.owner.id : null}
                            options={clients}
                            onChange={(e) =>
                              updateCategory(
                                index + 3,
                                "ownerId",
                                e.target.value
                              )
                            }
                            isRequired={category.isEdited}
                          />
                        </Grid>
                      </TableCell>
                      <TableCell>
                        <Grid item container direction="row" spacing="3">
                          <Grid item container direction="row">
                            <Grid item>
                              <Typography
                                component="div"
                                className="year-label"
                              >
                                Start &nbsp;
                              </Typography>
                            </Grid>
                            <Grid item>
                              <SelectMonth
                                value={category.startMonth}
                                year={category.startYear}
                                owner={category.owner}
                                planLength={calculateMaximumPlanLength(
                                  category
                                )}
                                client={client}
                                coClient={coClient}
                                onChange={(e) =>
                                  updateCategory(
                                    index + 3,
                                    "startMonth",
                                    e.target.value
                                  )
                                }
                                error={
                                  !checkDateRangeValid(
                                    category.startYear,
                                    category.startMonth,
                                    category.endYear,
                                    category.endMonth
                                  )
                                }
                              />
                              /&nbsp;
                              <SelectYear
                                value={category.startYear}
                                onChange={(e) =>
                                  updateCategory(
                                    index + 3,
                                    "startYear",
                                    e.target.value
                                  )
                                }
                                length={calculateMaximumPlanLength(category)}
                                error={
                                  !checkDateRangeValid(
                                    category.startYear,
                                    category.startMonth,
                                    category.endYear,
                                    category.endMonth
                                  )
                                }
                              />
                            </Grid>

                            <Grid item>
                              <Typography
                                component="div"
                                className="year-label"
                              >
                                End &nbsp;
                              </Typography>
                            </Grid>
                            <Grid item>
                              <SelectMonth
                                label="End"
                                value={category.endMonth}
                                year={category.endYear}
                                owner={category.owner}
                                client={client}
                                coClient={coClient}
                                planLength={calculateMaximumPlanLength(
                                  category
                                )}
                                onChange={(e) =>
                                  updateCategory(
                                    index + 3,
                                    "endMonth",
                                    e.target.value
                                  )
                                }
                                error={
                                  !checkDateRangeValid(
                                    category.startYear,
                                    category.startMonth,
                                    category.endYear,
                                    category.endMonth
                                  )
                                }
                              />
                              /&nbsp;
                              <SelectYear
                                from={category.endYear}
                                value={category.endYear}
                                onChange={(e) =>
                                  updateCategory(
                                    index + 3,
                                    "endYear",
                                    e.target.value
                                  )
                                }
                                length={calculateMaximumPlanLength(category)}
                                error={
                                  !checkDateRangeValid(
                                    category.startYear,
                                    category.startMonth,
                                    category.endYear,
                                    category.endMonth
                                  )
                                }
                              />
                            </Grid>
                            <Grid item>
                              {index >= 6 && (
                                <IconButton
                                  color="inherit"
                                  className="delete-button"
                                  onClick={() => deleteCategory(index + 3)}
                                  size="large"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid item container xs={12}>
            <Grid
              item
              container
              xs={3}
              direction="row"
              spacing="2"
              className="add-category"
              onClick={addNewCategory}
            >
              <Grid item>
                <img className="add-new" src={addIcon} alt="add-new-icon" />
              </Grid>
              <Grid item>
                <Typography component="div">Add new expense</Typography>
              </Grid>
            </Grid>
            <Grid item container xs={9} />
          </Grid>
        </Collapse>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    currentClient: state.currentClientReducer,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(ExpensesTab);
